// components/EquipmentData.js
import React, { useEffect, useState } from 'react';
// import styled from 'styled-components';
import { TextField, ListItem, ListItemText } from '@mui/material';

import { Label, BaseLabel, RowContainer, InputHalf, AutoComplete } from '../../../styles/styleUtils';
import CampoMonetario from '../components/CampoMonetario';

import { getKits, getKitsOffGrid } from '../../api';

// ── Produtos que possuem biblioteca de KIT ───────────────────────────────────
// Grid-Zero fica de fora porque ainda não tem biblioteca (aba em construção no
// cadastro de Kits). O roteamento abaixo espelha exatamente a tela de cadastro.
const PRODUTOS_KIT = [
  { key: 'ongrid', label: 'Gerador On-Grid' },
  { key: 'offgrid', label: 'Gerador Off-Grid' },
  { key: 'bombeamento', label: 'Sistema de Bombeamento' },
];

// Carrega a biblioteca de kits do produto, do mesmo jeito que o cadastro faz.
const carregarBiblioteca = (key) => {
  if (key === 'offgrid') return getKitsOffGrid();
  if (key === 'bombeamento') return getKits({ tipokat: 'bombeamento' });
  return getKits({}); // on-grid (sem tipokat, igual à aba 0 do cadastro)
};

// Descobre o produto a partir do discriminador do kit (usado em vendas importadas).
const tipokatParaKey = (kit) => {
  if (kit?.tipokat === 'offgrid') return 'offgrid';
  if (kit?.tipokat === 'bombeamento') return 'bombeamento';
  return 'ongrid';
};

function EquipmentData({ formData, onInputChange, onAutoCompleteChange, setFormData, dadosImportados }) {
  const [options, setOptions] = useState([]);
  const [produtoKey, setProdutoKey] = useState(() =>
    formData.produtoEquipamento ||
    (formData.kit && typeof formData.kit === 'object' ? tipokatParaKey(formData.kit) : null)
  );

  const travado = dadosImportados || formData.visualiza;

  // Mantém o produto em sincronia quando um kit já vem preenchido (ex.: importação).
  useEffect(() => {
    if (formData.kit && typeof formData.kit === 'object') {
      setProdutoKey(tipokatParaKey(formData.kit));
    }
  }, [formData.kit]);

  // Carrega a biblioteca de kits conforme o produto selecionado.
  useEffect(() => {
    let ativo = true;
    if (!produtoKey) {
      setOptions([]);
      return undefined;
    }
    // Em venda importada, o kit já está escolhido: só exibimos ele na lista.
    if (dadosImportados) {
      setOptions(formData.kit && typeof formData.kit === 'object' ? [formData.kit] : []);
      return undefined;
    }
    carregarBiblioteca(produtoKey)
      .then((dados) => {
        if (ativo) setOptions(Array.isArray(dados) ? dados : []);
      })
      .catch(() => {
        if (ativo) setOptions([]);
      });
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produtoKey, dadosImportados]);

  // Preenche o Valor do Kit automaticamente (à vista por padrão; cartão quando a
  // forma de pagamento for do tipo 'cartao'). Não roda em venda importada para
  // não sobrescrever o valor que veio da proposta. Continua editável pelo usuário.
  useEffect(() => {
    if (travado) return;
    const { kit } = formData;
    if (!kit || typeof kit !== 'object') return;
    const isCartao = formData.formaPagamento?.tipo === 'cartao';
    const preco = isCartao ? kit.valorcartao ?? kit.precovenda : kit.precovenda ?? kit.valorcartao;
    if (preco == null) return;
    if (Number(formData.valorKit) === Number(preco)) return; // evita update redundante
    setFormData((prev) => ({ ...prev, valorKit: preco }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.kit, formData.formaPagamento, dadosImportados]);

  const handleProdutoChange = (_, value) => {
    const novaKey = value?.key || null;
    setProdutoKey(novaKey);
    // Troca de produto zera o kit escolhido (bibliotecas diferentes).
    onAutoCompleteChange('idKit', 'kit', null);
    if (setFormData) {
      setFormData((prev) => ({ ...prev, produtoEquipamento: novaKey }));
    }
  };

  const produtoValue = PRODUTOS_KIT.find((p) => p.key === produtoKey) || null;

  return (
    <div>
      <RowContainer>
        <BaseLabel htmlFor="produtoEquipamento">Produto</BaseLabel>
        <AutoComplete
          disabled={travado}
          disablePortal
          id="produtoEquipamento"
          name="produtoEquipamento"
          value={produtoValue}
          onChange={handleProdutoChange}
          options={PRODUTOS_KIT}
          isOptionEqualToValue={(option, value) => option.key === value.key}
          getOptionLabel={(option) => option.label || ''}
          renderInput={(params) => <TextField {...params} placeholder="Selecione o produto" />}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="kit">KIT</BaseLabel>
        <AutoComplete
          disabled={travado || !produtoKey}
          disablePortal
          id="kit"
          name="kit"
          value={formData.kit || null}
          onChange={(_, value) => onAutoCompleteChange('idKit', 'kit', value)}
          options={options}
          isOptionEqualToValue={(option, value) => option._id === value._id}
          renderOption={(props, option) => (
            <ListItem key={option._id} {...props}>
              <ListItemText primary={option.nome} secondary={option.fornecedor?.nome} />
            </ListItem>
          )}
          getOptionLabel={(option) => `${option.nome}`}
          renderInput={(params) => (
            <TextField {...params} placeholder={produtoKey ? 'Selecione' : 'Selecione o produto primeiro'} />
          )}
        />
      </RowContainer>
      <RowContainer>
        <BaseLabel htmlFor="ncm">NCM</BaseLabel>
        <InputHalf
          type="text"
          id="ncm"
          name="ncm"
          value={formData.ncm}
          onChange={onInputChange}
          disabled={formData.visualiza}
        />
        <span />
        <span />
        <span />
        <Label htmlFor="valor">Valor do Kit</Label>

        <CampoMonetario
          name={'valorKit'}
          valor={formData.valorKit}
          onChange={onInputChange}
          disabled={dadosImportados || formData.visualiza}
        />
      </RowContainer>
    </div>
  );
}

export default EquipmentData;